/**
 * @module CircuitBreaker
 * @description
 * Implementation of the Circuit Breaker pattern for protecting external service calls.
 * Prevents cascade failures by failing fast when a service is unavailable.
 *
 * ## States
 * - CLOSED: Normal operation, requests pass through
 * - OPEN: Service unavailable, requests fail immediately
 * - HALF_OPEN: Testing if service has recovered
 *
 * ## Configuration
 * - failureThreshold: Number of failures before opening circuit (default: 3)
 * - cooldownPeriod: Time before attempting recovery (default: 30s)
 * - failureWindow: Time window to count failures (default: 60s)
 * - successThreshold: Successes needed to close from HALF_OPEN (default: 1)
 */

export type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

export interface CircuitBreakerConfig {
  /** Number of failures before opening circuit */
  failureThreshold: number;
  /** Time in ms before attempting recovery (HALF_OPEN) */
  cooldownPeriod: number;
  /** Time window in ms to count failures */
  failureWindow: number;
  /** Number of successful calls to close circuit from HALF_OPEN */
  successThreshold: number;
  /** Optional name for logging */
  name?: string;
  /** Optional callback when state changes */
  onStateChange?: (state: CircuitState, previousState: CircuitState) => void;
}

interface CircuitBreakerState {
  state: CircuitState;
  failureCount: number;
  lastFailureTime: number | null;
  successCount: number;
  lastStateChange: number;
}

export interface CircuitBreakerMetrics {
  totalCalls: number;
  successfulCalls: number;
  failedCalls: number;
  rejectedCalls: number;
  stateChanges: number;
  currentState: CircuitState;
}

export class CircuitOpenError extends Error {
  constructor(message: string = 'Circuit breaker is OPEN') {
    super(message);
    this.name = 'CircuitOpenError';
  }
}

export class CircuitBreaker<T> {
  private state: CircuitBreakerState;
  private config: CircuitBreakerConfig;
  private metrics: {
    totalCalls: number;
    successfulCalls: number;
    failedCalls: number;
    rejectedCalls: number;
    stateChanges: number;
  };
  private failureTimestamps: number[] = [];

  constructor(config: Partial<CircuitBreakerConfig> = {}) {
    this.config = {
      failureThreshold: config.failureThreshold ?? 3,
      cooldownPeriod: config.cooldownPeriod ?? 30000,
      failureWindow: config.failureWindow ?? 60000,
      successThreshold: config.successThreshold ?? 1,
      name: config.name ?? 'default',
      onStateChange: config.onStateChange,
    };

    this.state = {
      state: 'CLOSED',
      failureCount: 0,
      lastFailureTime: null,
      successCount: 0,
      lastStateChange: Date.now(),
    };

    this.metrics = {
      totalCalls: 0,
      successfulCalls: 0,
      failedCalls: 0,
      rejectedCalls: 0,
      stateChanges: 0,
    };
  }

  /**
   * Execute a function through the circuit breaker
   */
  async execute(fn: () => Promise<T>): Promise<T> {
    this.metrics.totalCalls++;

    // Check if circuit should transition from OPEN to HALF_OPEN
    if (this.state.state === 'OPEN') {
      const timeSinceLastChange = Date.now() - this.state.lastStateChange;
      if (timeSinceLastChange >= this.config.cooldownPeriod) {
        this.transitionTo('HALF_OPEN');
      }
    }

    // If circuit is OPEN, fail fast
    if (this.state.state === 'OPEN') {
      this.metrics.rejectedCalls++;
      console.warn(`[CircuitBreaker:${this.config.name}] Request rejected - circuit OPEN`);
      throw new CircuitOpenError(`Circuit breaker "${this.config.name}" is OPEN`);
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  private onSuccess(): void {
    this.metrics.successfulCalls++;

    if (this.state.state === 'HALF_OPEN') {
      this.state.successCount++;
      if (this.state.successCount >= this.config.successThreshold) {
        this.transitionTo('CLOSED');
      }
    } else if (this.state.state === 'CLOSED') {
      // Reset failure count on success in CLOSED state
      this.state.failureCount = 0;
      this.failureTimestamps = [];
    }
  }

  private onFailure(): void {
    this.metrics.failedCalls++;
    const now = Date.now();

    // Clean up old failures outside the window
    this.failureTimestamps = this.failureTimestamps.filter(
      ts => now - ts < this.config.failureWindow
    );

    this.failureTimestamps.push(now);
    this.state.failureCount = this.failureTimestamps.length;
    this.state.lastFailureTime = now;

    console.warn(
      `[CircuitBreaker:${this.config.name}] Failure recorded (${this.state.failureCount}/${this.config.failureThreshold})`
    );

    if (this.state.state === 'HALF_OPEN') {
      // Any failure in HALF_OPEN reopens the circuit
      this.transitionTo('OPEN');
    } else if (this.state.state === 'CLOSED') {
      if (this.state.failureCount >= this.config.failureThreshold) {
        this.transitionTo('OPEN');
      }
    }
  }

  private transitionTo(newState: CircuitState): void {
    if (this.state.state !== newState) {
      const previousState = this.state.state;
      this.state.state = newState;
      this.state.lastStateChange = Date.now();
      this.state.successCount = 0;
      this.metrics.stateChanges++;

      console.info(
        `[CircuitBreaker:${this.config.name}] State transition: ${previousState} -> ${newState}`
      );

      if (newState === 'CLOSED') {
        this.state.failureCount = 0;
        this.failureTimestamps = [];
      }

      // Call state change callback
      if (this.config.onStateChange) {
        this.config.onStateChange(newState, previousState);
      }
    }
  }

  /**
   * Get current circuit state (checks for automatic HALF_OPEN transition)
   */
  getState(): CircuitState {
    if (this.state.state === 'OPEN') {
      const timeSinceLastChange = Date.now() - this.state.lastStateChange;
      if (timeSinceLastChange >= this.config.cooldownPeriod) {
        return 'HALF_OPEN';
      }
    }
    return this.state.state;
  }

  /**
   * Get circuit metrics
   */
  getMetrics(): CircuitBreakerMetrics {
    return {
      ...this.metrics,
      currentState: this.getState(),
    };
  }

  /**
   * Check if circuit is open (requests will be rejected)
   */
  isOpen(): boolean {
    return this.getState() === 'OPEN';
  }

  /**
   * Check if circuit is closed (normal operation)
   */
  isClosed(): boolean {
    return this.getState() === 'CLOSED';
  }

  /**
   * Check if circuit is half-open (testing recovery)
   */
  isHalfOpen(): boolean {
    return this.getState() === 'HALF_OPEN';
  }

  /**
   * Manually reset circuit to CLOSED state
   */
  reset(): void {
    const previousState = this.state.state;
    this.state = {
      state: 'CLOSED',
      failureCount: 0,
      lastFailureTime: null,
      successCount: 0,
      lastStateChange: Date.now(),
    };
    this.failureTimestamps = [];

    console.info(`[CircuitBreaker:${this.config.name}] Manually reset from ${previousState} to CLOSED`);

    if (previousState !== 'CLOSED' && this.config.onStateChange) {
      this.config.onStateChange('CLOSED', previousState);
    }
  }

  /**
   * Get time until circuit attempts recovery (when OPEN)
   */
  getTimeUntilRetry(): number | null {
    if (this.state.state !== 'OPEN') {
      return null;
    }
    const elapsed = Date.now() - this.state.lastStateChange;
    return Math.max(0, this.config.cooldownPeriod - elapsed);
  }
}

// ============================================
// CIRCUIT BREAKER REGISTRY
// ============================================

/**
 * Global registry for managing multiple circuit breakers
 */
class CircuitBreakerRegistry {
  private breakers: Map<string, CircuitBreaker<unknown>> = new Map();
  private stateChangeListeners: Array<(name: string, state: CircuitState) => void> = [];

  /**
   * Get or create a circuit breaker
   */
  getOrCreate<T>(name: string, config?: Partial<CircuitBreakerConfig>): CircuitBreaker<T> {
    if (!this.breakers.has(name)) {
      const breaker = new CircuitBreaker<T>({
        ...config,
        name,
        onStateChange: (state, prev) => {
          this.notifyStateChange(name, state);
          config?.onStateChange?.(state, prev);
        },
      });
      this.breakers.set(name, breaker as CircuitBreaker<unknown>);
    }
    return this.breakers.get(name) as CircuitBreaker<T>;
  }

  /**
   * Get a circuit breaker by name
   */
  get<T>(name: string): CircuitBreaker<T> | undefined {
    return this.breakers.get(name) as CircuitBreaker<T> | undefined;
  }

  /**
   * Get all circuit breaker states
   */
  getAllStates(): Record<string, CircuitState> {
    const states: Record<string, CircuitState> = {};
    this.breakers.forEach((breaker, name) => {
      states[name] = breaker.getState();
    });
    return states;
  }

  /**
   * Check if any circuit is open
   */
  hasOpenCircuits(): boolean {
    for (const breaker of this.breakers.values()) {
      if (breaker.isOpen()) {
        return true;
      }
    }
    return false;
  }

  /**
   * Subscribe to state changes
   */
  onStateChange(listener: (name: string, state: CircuitState) => void): () => void {
    this.stateChangeListeners.push(listener);
    return () => {
      const index = this.stateChangeListeners.indexOf(listener);
      if (index >= 0) {
        this.stateChangeListeners.splice(index, 1);
      }
    };
  }

  private notifyStateChange(name: string, state: CircuitState): void {
    this.stateChangeListeners.forEach(listener => listener(name, state));
  }

  /**
   * Reset all circuit breakers
   */
  resetAll(): void {
    this.breakers.forEach(breaker => breaker.reset());
  }
}

// Global singleton instance
export const circuitBreakerRegistry = new CircuitBreakerRegistry();

// ============================================
// PRE-CONFIGURED CIRCUIT BREAKERS
// ============================================

/**
 * Circuit breaker for Google Gemini AI service
 */
export const geminiCircuitBreaker = circuitBreakerRegistry.getOrCreate<string>('gemini', {
  failureThreshold: 3,
  cooldownPeriod: 30000, // 30 seconds
  failureWindow: 60000,  // 1 minute
  successThreshold: 1,
});

/**
 * Circuit breaker for Supabase database
 */
export const supabaseCircuitBreaker = circuitBreakerRegistry.getOrCreate<unknown>('supabase', {
  failureThreshold: 5,
  cooldownPeriod: 15000, // 15 seconds (faster recovery for DB)
  failureWindow: 60000,
  successThreshold: 2,
});

export default CircuitBreaker;
